import { Box, Button, IconButton, Typography, useTheme, Tooltip, Dialog, CircularProgress, Collapse } from '@mui/material'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useQuizAndQuestionbankStore } from '../../../store/quiz.store'
import { observer } from 'mobx-react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Icons, latexDelimiters, formatDate, getTransformedGroupDetails, RED_SHADE_TABLE_HIGHLIGHT_COLOR } from '../../../utils/utilities'
import DataLoadingSpinner from '../../../components/common/DataLoadingSpinner'
import QuestionImportFileInputDialog from '../../../components/exams-components/QuestionImportFileInputDialog'
import QuestionsPreviewDialog from '../../../components/exams-components/QuestionsPreviewDialog'
import CreateQuizInstanceDialog from '../../../components/exams-components/quiz-questionbank/CreateQuizInstanceDialog'
import { toast } from 'react-toastify'
import { useSubjectStore } from '../../../store/subject.store'
import api from '../../../service/api'
import ConfirmDialog from '../../../components/common/ConfirmDialog'
import InstanceBox from '../../../components/exams-components/quiz-questionbank/InstanceBox'
import { exportQuizReport } from '../../../utils/exam-utilities'
import Latex from 'react-latex-next'
import CustomTable from '../../../components/common/custom-table/CustomTable'
import CreateOrEditQuizInstanceDialog from '../../../components/exams-components/quiz-questionbank/CreateQuizInstanceDialog'
import { AccessTimeFilled, CopyAllOutlined } from '@mui/icons-material'
import BreadcrumbList from '../../../components/common/BreadcrumbList'
import CustomChip from '../../../components/common/CustomChip'


const ScoreViewer = ( { scoreDetails, setScoreDetails, loadingScoreDetails } ) => {

    const headerRef = useRef()


    const { palette, border } = useTheme()
    const [headerHeight, setHeaderHeight] = useState( '65px' )

    useEffect( () => {
        if ( scoreDetails && headerRef.current )
            setHeaderHeight( `${headerRef?.current?.offSetHeight || 65}px` )
    }, [scoreDetails] )

    return (
        <Dialog open={Boolean( scoreDetails )} slotProps={{
            paper: { className: 'slim-custom-scrollbar', sx: { width: "60%", maxWidth: "600px" } }
        }} >
            <Box bgcolor={palette.background.paper} flexGrow={1} display="flex" flexDirection="column">
                <Box ref={headerRef} position="sticky" top="0" display="flex" alignItems="center" justifyContent="space-between" borderBottom={border[1]} padding="10px 20px" bgcolor={palette.background.paper}>
                    <Box maxHeight="45px">
                        <Typography variant="h6" fontSize="16px">{scoreDetails?.instance}</Typography>
                        <Typography variant="subtitle2" fontSize="12px">{scoreDetails?.student_details?.name}</Typography>
                    </Box>
                    <IconButton size="small" onClick={() => setScoreDetails( null )}>
                        {Icons.default.CancelIcon}
                    </IconButton>
                </Box>
                <Box >
                    <Box bgcolor={palette.contentBg} position="sticky" top={headerHeight} padding="10px 20px" display="flex" alignItems="center" gap="30px">
                        <Typography variant="h6">OVERALL SCORE</Typography>
                        <Typography variant="h6"> <strong>{scoreDetails?.answers?.total_score} / {scoreDetails?.answers?.max_score}</strong></Typography>
                    </Box>
                    <Box display="flex" gap="20px" alignItems="center" borderBottom={border[1]} padding="20px">
                        <Box display="flex" gap="10px" alignItems="center">
                            <Box sx={{ width: "15px", borderRadius: "50%", height: "15px", border: `2px solid ${palette.success.dark}` }}></Box>
                            <Typography variant='subtitle2' fontSize="12px" >Correct option</Typography>
                        </Box>
                        <Box display="flex" gap="10px" alignItems="center">
                            <Box sx={{ width: "15px", borderRadius: "50%", bgcolor: palette.success.dark, height: "15px", border: `2px solid ${palette.success.dark}` }}></Box>
                            <Typography variant='subtitle2' fontSize="12px" >Answered, correctly</Typography>
                        </Box>
                        <Box display="flex" gap="10px" alignItems="center">
                            <Box sx={{ width: "15px", borderRadius: "50%", bgcolor: palette.error.light, height: "15px", border: `2px solid ${palette.error.dark}` }}></Box>
                            <Typography variant='subtitle2' fontSize="12px" >Answered, but incorrect</Typography>
                        </Box>
                    </Box>

                    <Box display="flex" padding="0 20px 20px 20px" marginTop="30px" flexDirection="column" gap="20px">
                        {Object.keys( scoreDetails?.answers?.scores || {} ).map( ( quesId, index ) => {
                            const ques = scoreDetails?.answers?.scores[quesId]
                            return <Box borderBottom={border[1]} paddingBottom="20px" key={index}>
                                {ques.given < 0 ? <Typography padding="5px 10px" marginBottom="20px" borderRadius="5px" width="fit-content" bgcolor={palette.errorMessage.light + 22} variant='subtitle2' color="errorMessage.main"><em>Not Answered</em></Typography> :
                                    <Typography padding="5px 10px" marginBottom="20px" borderRadius="5px" width="fit-content" bgcolor={palette.primaryDark.light + 22} variant='subtitle2' color="primaryDark.main"><em>Answered</em></Typography>}
                                <Box color={palette.contrstText} className='questionbank-wysiwyg-container' fontWeight="bold" marginTop="-5px" fontSize="14px">
                                    <Latex delimiters={latexDelimiters}>{ques.question_text}</Latex>
                                </Box>
                                <Typography variant='body2' color="textSecondary"><em>{ques.selection_type}</em></Typography>
                                <Box marginTop="10px" display="flex" flexDirection="column" gap="10px">
                                    {ques.options.map( ( option, optionIndex ) => {
                                        const isCorrect = ques.selection_type === "single" ? +ques.correct_answer === optionIndex : ques.options_status[optionIndex]
                                        const hasAnswered = ques.selection_type === "single" ? +ques.given === optionIndex : ques.given.includes( optionIndex )
                                        return (
                                            <Box
                                                padding="10px"
                                                border={border[1]}
                                                borderRadius="5px"
                                                bgcolor={hasAnswered ? ( hasAnswered && isCorrect ) ? "success.dark" : "error.light" : "none"}
                                                borderColor={isCorrect ? "success.dark" : hasAnswered ? "errorMessage.dark" : palette.borderColor}
                                                sx={{ borderWidth: isCorrect || hasAnswered ? "2px" : "1px" }}
                                                display="flex"
                                                marginBottom="2px"
                                                alignItems="flex-start"
                                                gap="10px"
                                                key={optionIndex}
                                            >
                                                <Typography padding="2px 2px 0px 2px" variant="subtitle2" fontSize="12px" sx={{ aspectRatio: 1 / 1 }} bgcolor={palette.greyedOut} border={`1px solid ${palette.borderColor}`} display="flex" alignItems="center" justifyContent="center" width='20px' borderRadius="50%"
                                                    fontWeight={( isCorrect || hasAnswered ) ? "bolder" : "regular"} color={isCorrect ? "success" : hasAnswered ? "error" : palette.common.font}>{optionIndex + 1}</Typography>
                                                <Box variant="subtitle2" className='questionbank-wysiwyg-container' color={( hasAnswered ) ? "white" : palette.common.font}>
                                                    <Latex delimiters={latexDelimiters}>{option.option_text}</Latex>
                                                </Box>
                                            </Box>
                                        )
                                    } )}
                                </Box>
                            </Box>
                        } )}
                    </Box>
                </Box>
            </Box>
        </Dialog>
    )
}

const rowStylesGetter = row => {
    if ( row.no_attempt ) {
        return {
            "--bg-color": t => t.theme === 'dark' ? t.darken( RED_SHADE_TABLE_HIGHLIGHT_COLOR, 0.5 ) : t.lighten( t.palette.error.main, 0.8 ),
            ":hover": {},
            cursor: "default"
        }
    }
    else return {}
}

const QuizScores = ( { quizId, quizTitle, instance, instancesCount, onBack, onReschedule, onCopyLink } ) => {

    const [loadingScoreDetails, setLoadingScoreDetails] = useState( false )
    const [legacyGroup, setLegacyGroup] = useState( false )
    const [loadingScores, setLoadingScores] = useState( true )
    const [scoreDetails, setScoreDetails] = useState( null )
    const [scores, setScores] = useState( [] )

    const { palette, border, table, layout } = useTheme()

    const fetchScoreDetails = async attemptDetails => {
        setLoadingScoreDetails( attemptDetails.attempt_id )
        try {
            let instanceTitle = ''
            if ( instance?.access_group?.section_assignment_id ) {
                instanceTitle = `${instance.access_group.course_branch_short_name ? instance.access_group.course_branch_short_name + "," : ""} ${instance.access_group?.current_year ? `year: ${instance.access_group?.current_year},` : ""} ${instance.access_group.current_sem ? `Sem: ${instance.access_group.current_sem},` : ""} Sec: ${instance.access_group.section_short_name}`
            } else {
                instanceTitle = `${instance.access_group.course_name_short ? instance.access_group.course_name_short + "," : ""} ${instance.access_group?.current_year ? `year: ${instance.access_group?.current_year},` : ""} ${instance.access_group.current_sem ? `Sem: ${instance.access_group.current_sem},` : ""} Batch: ${instance.access_group.batch_short_name}`
            }
            const { data: { data } } = await api.quiz.fetchAttemptScore( attemptDetails.quiz_id, attemptDetails.quiz_instance_id, attemptDetails.attempt_id )
            setScoreDetails( { answers: data, student_details: attemptDetails.student_details, instance: instanceTitle } )
        } catch ( err ) {
            console.log( err )
            toast( err?.response?.data?.message || "Something went wrong whie fetching scores!" )
        } finally {
            setLoadingScoreDetails( false )
        }
    }

    useEffect( () => {
        let active = true
        const fetchScores = async () => {
            setLoadingScores( true )
            try {
                let groupId = instance?.access_group?.id
                const params = {}
                if ( instance?.access_group?.batch_assignment_id )
                    params.batch_assignment_id = groupId
                else
                    params.section_assignment_id = String( groupId || "" ).split( "-" ).at( 0 )
                const [scoresResponse, studentsResponse] = await Promise.all( [
                    api.quiz.fetchQuizInstanceScores( quizId, instance.id ),
                    api.quiz.fetchBatchOrSectionAssignedStudentDetails( params ),
                ] )


                const fetchedScores = scoresResponse?.data?.scores || []
                const studentResponseData = studentsResponse?.data?.data
                const assignedStudents = studentResponseData || []

                if ( assignedStudents.length === 0 )
                    setLegacyGroup( true )

                const attemptedScores = new Map( fetchedScores.map( score => [String( score.student_id ), score] ) )
                const unattempted = []
                assignedStudents.forEach( student => {
                    const studentId = student.auid
                    const attemptedScore = attemptedScores.get( String( studentId ) )
                    if ( attemptedScore )
                        return
                    return unattempted.push( {
                        id: studentId,
                        no_attempt: true,
                        student_id: studentId,
                        student_details: {
                            name: student.student_name || student.name || student.student_details?.name,
                            usn: student.usn,
                        },
                        max_score: fetchedScores[0]?.max_score || 0,
                        total_score: "No attempt",
                        attempted: false,
                    } )
                } )

                if ( active ) setScores( [...fetchedScores, ...unattempted] )
            } catch ( err ) {
                console.log( err )
                toast( err?.response?.data?.message || "Something went wrong while fetching scores!" )
                if ( active ) onBack()
            } finally {
                if ( active ) setLoadingScores( false )
            }
        }
        fetchScores()
        return () => { active = false }
    }, [instance, onBack, quizId] )

    const scoreColumns = [
        { field: "student_name", name: "Name", searchable: true, sortable: true, renderValue: score => score.student_details?.name, renderCell: score => <>{score.student_details?.name}{loadingScoreDetails === score.attempt_id && <DataLoadingSpinner color='secondary' waitingMessage="Loading details..." padding='0' size={14} />}</> },
        { field: "student_id", name: "AUID", searchable: true, sortable: true },
        { field: "usn", name: "USN", renderValue: score => score?.student_details?.usn, renderCell: score => score?.student_details?.usn || "-", searchable: true, sortable: true },
        { field: "max_score", name: "Total Marks", align: "center", sortable: true, dataType: "number" },
        { field: "total_score", name: "Marks Scored", align: "center", sortable: true, dataType: "number" },
    ]

    return (
        <Box>
            <ScoreViewer scoreDetails={scoreDetails} setScoreDetails={setScoreDetails} loadingScoreDetails={loadingScoreDetails} />
            <Box sx={{ p: 1 }} overflow="hidden" border={border[1]} bgcolor={palette.background.paper} borderRadius="5px">
                <Box display="flex" padding="10px" bgcolor={table.contentBg} flexWrap="wrap" gap="20px" justifyContent="space-between" alignItems="center">
                    {instancesCount > 1 && <Button color='greyed' startIcon={Icons.default.ArrowBack} variant='outlined' size="small" onClick={onBack}>Back to Instances</Button>}
                    <Box display="flex" width="100%" alignItems="center" gap="10px" flexWrap="wrap-reverse" justifyContent="space-between" flexGrow={1}>
                        <Box>
                            <Typography variant='subtitle1'>Quiz Scores</Typography>
                            <Box display="flex" gap="5px" alignItems="center" flexWrap="wrap">
                                {instance?.access_group && <CustomChip tooltipTitle={getTransformedGroupDetails( instance.access_group ).academic_details} value={getTransformedGroupDetails( instance.access_group ).short_title} />}
                                <CustomChip tooltipTitle='Quiz date' value={formatDate( instance?.start_date )} />
                            </Box>
                        </Box>
                        <Box flexShrink={0} display="flex" alignItems="center" gap="10px">
                            <Button variant='outlined' disableElevation size='small' color='secondary' onClick={() => onCopyLink( instance )} startIcon={<CopyAllOutlined />} sx={{ textTransform: "capitalize", width: "100%", margin: 0, justifyContent: "flex-start" }}>Copy link</Button>
                            {( new Date() < new Date( `${instance?.start_date?.split( "T" )[0]} ${instance?.start_time}` ) || new Date() > new Date( `${instance?.start_date?.split( "T" )[0]} ${instance?.end_time}` ) ) && <Button variant='outlined' disableElevation size='small' onClick={onReschedule} startIcon={<AccessTimeFilled />} color='primary' sx={{ textTransform: "capitalize", width: "100%", margin: 0, justifyContent: "flex-start" }}>Reschedule</Button>}
                        </Box>
                    </Box>
                </Box>
                {loadingScores && <DataLoadingSpinner waitingMessage="Loading scores..." />}

                {legacyGroup && <Typography sx={{ m: 0.5, fontSize: "0.875rem", display: "flex", gap: 1, alignItems: "flex-start", p: 1, borderRadius: layout.borderRadius, bgcolor: palette.warning.light + "22", color: palette.warning.main }} >
                    <Typography component="span" sx={{ color: "inherit", fontSize: "1.2rem" }} >{Icons.default.Warning}</Typography>
                    The batch or section assigned to this resource belongs to a previous academic year and is no longer active, so the list of students who did not attempt this quiz cannot be displayed.
                </Typography>}

                {!loadingScores && <CustomTable
                    rowStyles={rowStylesGetter}
                    data={scores}
                    columns={scoreColumns}
                    dataIdKey="id"
                    globalSearch="Search students"
                    tableKey="quizScores"
                    onRowClick={row => row.attempted !== false && fetchScoreDetails( row )}
                    tableStyles={{ maxHeight: "350px" }}
                    noDataBlock={<Typography border={`2px solid ${palette.warning.dark}`} color='warning' bgcolor={palette.warning.light + "22"} margin="10px" padding="20px" borderRadius="5px" display="flex" gap="5px" alignItems="center" variant='subtitle2'>{Icons.default.Warning}There are no participations for this quiz!</Typography>}
                    customActions={[
                        { label: "Export To Excel", type: "button", startIcon: Icons.default.UploadFileIcon, onClick: () => exportQuizReport( scores, quizTitle, instance?.start_date, legacyGroup ) }
                    ]}
                />}
            </Box>
        </Box>
    )
}

const Quiz = observer( ( { fromComponent = 'subject' } ) => {

    const [searchParams, setSearchParams] = useSearchParams( { mode: "instances", selectedQuizInstance: "", searchText: "" } )
    const mode = searchParams.get( "mode" )
    const selectedQuizInstance = searchParams.get( "selectedQuizInstance" )

    const [loading, setLoading] = useState( true )
    const [deleting, setDeleting] = useState( false )
    const [showTopics, setShowTopics] = useState( false )
    const [isImporting, setIsImporting] = useState( false )
    const [editDialogState, setEditDialogState] = useState( false )
    const [questionsPreview, setQuestionsPreview] = useState( null )
    const [deleteDialogState, setDeleteDialogState] = useState( false )
    const [selectedQuizInstanceId, setSelectedQuizInstanceId] = useState( selectedQuizInstance || null )
    const [questionsImportDialogState, setQuestionsImportDialogState] = useState( false )
    const [createQuizInstanceDialogState, setCreateQuizInstanceDialogState] = useState( false )

    const { id, subject_code } = useParams()
    const navigate = useNavigate()
    const { palette, border } = useTheme()

    const QuizStore = useQuizAndQuestionbankStore()
    const SubjectStore = useSubjectStore()
    const selectedInstanceDetails = QuizStore.getQuiz?.instances?.find( instance => +instance.id === +selectedQuizInstanceId )


    const setSearchParamValue = useCallback( ( keys, vals = "" ) => {
        const keysList = keys?.split( "," ) || []
        const values = vals?.split( "," ) || []
        setSearchParams( prev => {
            const newParams = new URLSearchParams( prev )
            keysList.forEach( ( key, i ) => {
                newParams.set( key, values[i] || "" )
            } )
            return newParams
        }, { replace: true } )
    }, [setSearchParams] )

    const backToInstances = useCallback( () => {
        setSelectedQuizInstanceId( null )
        setSearchParamValue( 'mode,selectedQuizInstance', "instances," )
    }, [] )

    const deleteQuiz = async () => {
        setDeleting( true )
        try {
            await api.quiz.deleteQuiz( id )
            await QuizStore.fetchQuizes()
            navigate( subject_code ? `/faculty/subject/${SubjectStore.getSubject.subject_id}/quiz` : `/faculty/quizzes` )
            QuizStore.fetchClassAssociatedQuizInstances()
        } catch ( err ) {
            toast( err?.response?.data?.message || "Something went wrong while deleting the quiz!" )
        } finally {
            setDeleting( false )
        }
    }

    const copyQuizLinkForStudent = details => {
        const link = `${window.location.origin}/student/quiz?quiz_id=${details.quiz_id}&instance_id=${details.id}`
        navigator.clipboard.writeText( link )
        toast( <span>Link copied <strong> <em>{link}</em> </strong> </span> )
    }

    const getData = useCallback( async () => {
        const { success, message } = await QuizStore.fetchQuiz( id )
        if ( !success )
            navigate( `/faculty/quizzes` )
        else {
            if ( QuizStore.getQuiz?.instances?.length === 1 ) {
                setSelectedQuizInstanceId( QuizStore.getQuiz?.instances[0]?.id )
            }
        }
        setLoading( false )
    }, [QuizStore, id, navigate] )


    useEffect( () => {
        getData()
    }, [getData] )

    useEffect( () => {
        if ( selectedQuizInstanceId && QuizStore.getQuiz?.instances?.length > 0 )
            setSearchParamValue( 'mode,selectedQuizInstance', `scores,${selectedQuizInstanceId}` )
    }, [QuizStore, selectedQuizInstanceId, setSearchParamValue] )

    return (
        <Box flexGrow={1} padding="20px" display="flex" flexDirection="column" overflow="auto">
            <QuestionImportFileInputDialog setQuestionsPreview={setQuestionsPreview} questionsPreview={questionsPreview} isImporting={isImporting} importDialogStatus={questionsImportDialogState} setImportDialogStatus={setQuestionsImportDialogState} />
            <QuestionsPreviewDialog setIsImporting={setIsImporting} getQuestions={() => { }} examId={id} parent="quiz" setQuestionsPreview={setQuestionsPreview} questionsPreview={questionsPreview} />
            <CreateQuizInstanceDialog state={createQuizInstanceDialogState} setState={setCreateQuizInstanceDialogState} />
            <ConfirmDialog message="All the associated instances and their data will be deleted!" actionName="Yes! delete it" confirmAction={deleteQuiz} waiting={deleting} status={deleteDialogState} cancelAction={() => setDeleteDialogState( false )} />
            {editDialogState && Boolean( selectedInstanceDetails ) && <CreateOrEditQuizInstanceDialog setState={setEditDialogState} state={editDialogState} instance={selectedInstanceDetails} mode='edit' />}


            <BreadcrumbList items={[
                { label: "Quizzes", link: "/faculty/quizzes" },
                loading ? <CircularProgress size={14} /> : QuizStore.getQuiz?.details?.title
            ]} />
            <Box sx={{ display: "flex", flexDirection: "column", flexGrow: 1 }} gap="20px" >

                {!loading && <Box display="flex" justifyContent="space-between" flexWrap="wrap" alignItems="center" gap="20px">
                    <Box >
                        <Typography variant="h1" fontWeight="600" component="h1" fontSize="16px">
                            Quiz - {QuizStore.getQuiz?.details?.title}
                        </Typography>
                        <Typography variant="subtitle2" fontSize="12px" color='textSecondary' >{QuizStore.getQuiz?.details?.subject?.subject_name}</Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap="10px">
                        {/* <Button onClick={() => setCreateQuizInstanceDialogState( true )} variant='contained' startIcon={Icons.default.AddIcon} disableElevation sx={{ textTransform: "capitalize", width: "max-content" }} size="small" color="primary">
                            Create instance
                        </Button> */}
                        <Button variant='text' onClick={() => setDeleteDialogState( true )} startIcon={Icons.default.DeleteIcon} disableElevation sx={{ textTransform: "capitalize", width: "max-content" }} size="small" color="error">
                            delete
                        </Button>
                    </Box>
                </Box>}
                {!loading && <Box border={border[1]} borderRadius="5px">
                    <Tooltip title="Click to view topics" >
                        <Typography onClick={() => setShowTopics( prev => !prev )} sx={{ cursor: "pointer", ":hover": { bgcolor: palette.contentBg } }} borderBottom={border[1]} bgcolor={palette.contentBg + 99} variant='subtitle2' padding="10px" display="flex" alignItems="center" gap="5px" >
                            {QuizStore.getQuiz.details?.question_bank_name}
                            <Link className='default-link' to={!subject_code ? `/faculty/questionbank/${QuizStore.getQuiz.details?.question_bank}` : `/faculty/subject/${SubjectStore.getSubject.subject_code}/questionbank/${QuizStore.getQuiz.details?.question_bank}`}>
                                {Icons.default.OpenInNewIcon}
                            </Link>
                        </Typography>
                    </Tooltip>
                    <Collapse in={showTopics}>
                        <Box display="flex" gap="10px" flexWrap="wrap" padding="10px">
                            {QuizStore.getQuiz.details?.question_bank_topics?.map( ( topic, index ) => {
                                return (
                                    <Tooltip key={topic.id} title={`${topic.question_count} questions`} >
                                        <Box padding="5px 10px" borderRadius="5px" border={`1px solid ${palette.text.secondary}`}>
                                            <Typography fontSize="12px" variant='subtitle2'>{topic?.name}</Typography>
                                            <Typography fontSize="10px" color='textSecondary' component="span" display="block" borderRadius="5px">{topic.question_count} Questions</Typography>
                                        </Box>
                                    </Tooltip>
                                )
                            } )}
                        </Box>
                    </Collapse>
                </Box>}

                {!loading && <Box borderRadius="5px">
                    {mode === "instances" && QuizStore.getQuiz && <Box overflow="hidden" borderRadius="5px">
                        <Typography variant='subtitle2' letterSpacing={"0.1em"} color='textSecondary'>QUIZ INSTANCES</Typography>
                        <Box display="grid" gridTemplateColumns={{ lg: "1fr 1fr 1fr", md: "1fr 1fr 1fr", sm: "1fr 1fr", xs: "1fr" }} marginTop="10px" gap="10px" borderRadius="5px">
                            {QuizStore.getQuiz?.instances?.length > 1 && QuizStore.getQuiz?.instances?.map( ( instance, index ) => {
                                let title = ''
                                if ( instance?.access_group?.section_assignment_id ) {
                                    title = `${instance.access_group.course_branch_short_name ? instance.access_group.course_branch_short_name + "," : ""} ${instance.access_group?.current_year ? `year: ${instance.access_group?.current_year},` : ""} ${instance.access_group.current_sem ? `Sem: ${instance.access_group.current_sem},` : ""} Sec: ${instance.access_group.section_short_name}`
                                } else
                                    title = `${instance.access_group.course_name_short ? instance.access_group.course_name_short + "," : ""} ${instance.access_group?.current_year ? `year: ${instance.access_group?.current_year},` : ""} ${instance.access_group.current_sem ? `Sem: ${instance.access_group.current_sem},` : ""} Batch: ${instance.access_group.batch_short_name}`


                                return <InstanceBox isSelected={+selectedQuizInstance === instance.id} key={index} refetchQuizInstances={getData} title={title} setSelectedQuizInstanceId={setSelectedQuizInstanceId} instance={instance} />
                            } )}
                            {QuizStore.getQuiz?.instances?.length === 0 && <Typography variant="subtitle2" color="GrayText">No Quiz Instances</Typography>}
                        </Box>
                    </Box>}
                    {!loading && mode === "scores" && QuizStore.getQuiz && selectedInstanceDetails && <QuizScores
                        quizId={id}
                        quizTitle={QuizStore.getQuiz?.details?.title}
                        instance={selectedInstanceDetails}
                        instancesCount={QuizStore.getQuiz?.instances?.length}
                        onBack={backToInstances}
                        onReschedule={() => setEditDialogState( true )}
                        onCopyLink={copyQuizLinkForStudent}
                    />}
                </Box>}
                {loading && <DataLoadingSpinner waitingMessage="Loading your quiz..." />}
            </Box >
        </Box >
    )
} )

export default Quiz
